/**
 * assets/dm-accordion.js — the exclusive-open closer for `dm-accordion` groups.
 *
 * WHAT THIS FILE IS NOT. It is not the accordion. Opening, closing, keyboard
 * operation and expanded/collapsed announcement are all native to the details
 * element that `snippets/dm-accordion.liquid` emits, and all of them work with
 * this script blocked, failed or still in flight.
 *
 * THE ONE THING IT ADDS, AND THE FAILURE IT COVERS. Exclusive accordions —
 * opening one row closes its siblings — are native via the `name` attribute,
 * but only on Chrome 120 / Safari 17.2 / Firefox 130 and later. On the iOS
 * 16/17 tail the attribute is parsed, kept, and IGNORED: every panel can be
 * open at once, with no error and nothing in the console. On the PDP buy-box
 * stack that is a visible failure on the highest-value surface in the store,
 * which is why the degradation is also stated in the section's schema `info`.
 *
 * The detection is a property test on a detached element, evaluated once at
 * script scope: `'name' in document.createElement('details')`. On a supporting
 * browser `init()` binds nothing at all.
 *
 * WHY THE LISTENER IS CAPTURING. The `toggle` event does not bubble. A
 * capturing listener on the group still sees it, because capture propagates to
 * ancestors regardless of bubbling — so one listener per group covers every
 * row, including rows the theme editor adds after mount.
 *
 * Every listener goes through `this.on()` (dm-core.js), so it is bound to the
 * mount's AbortController signal and released when the theme editor replaces
 * the section's subtree. Never a raw addEventListener.
 *
 * Classic script, ES2020, zero dependencies beyond `window.DMElement`.
 */
(function () {
  'use strict';

  /** True when the browser implements exclusive `details name` groups. */
  const NATIVE_EXCLUSIVE = 'name' in document.createElement('details');

  class DMAccordion extends DMElement {
    init() {
      if (NATIVE_EXCLUSIVE) return;

      this.on(
        this,
        'toggle',
        (event) => {
          const opened = event.target;
          if (!opened || opened.tagName !== 'DETAILS' || !opened.open) return;

          // Read the ATTRIBUTE, not the property: on exactly the browsers this
          // branch exists for, the property is the thing that is missing.
          const group = opened.getAttribute('name');
          if (!group) return;

          this.querySelectorAll('details[name="' + group + '"][open]').forEach((row) => {
            if (row !== opened) row.open = false;
          });
        },
        { capture: true }
      );
    }

    /**
     * dm-core's `shopify:block:select` opt-in. A block inside a closed panel is
     * invisible to the merchant who just clicked it in the editor sidebar, and
     * this is the hook dm-core.js documents for exactly that case. Bound only
     * in the editor, and only because this component can hide its own blocks.
     */
    revealBlock(target) {
      const row = target.closest('details');
      if (row) row.open = true;
    }
  }

  DMElement.define('dm-accordion', DMAccordion);
})();
